import { Permission } from '../../api';
import { PATHS } from '../../routes/paths';
import type { Area } from '../../session';

/**
 * What each area's navigation offers.
 *
 * Two separate lists rather than one list filtered by role. Filtering reads as
 * "the same product, with bits hidden", and that is not what this is: the
 * back-office and the portal are different products that happen to share a
 * shell. Keeping them apart here means a back-office screen added later cannot
 * appear in a customer's sidebar because someone got a predicate wrong.
 *
 * One item carries a permission, and it is the exception that proves the rule.
 * The certificate screen is refused to every staff role but the administrator,
 * so leaving it in an analyst's sidebar would be offering them a `403` -- which
 * is not "the same product with bits hidden", it is a link that does not work.
 * See {@link NavItem.requires}.
 */
export interface NavItem {
  readonly to: string;
  readonly label: string;
  /** Matched exactly rather than by prefix. Needed for each area's own home. */
  readonly end?: boolean;
  /**
   * A permission the signed-in principal must hold for this item to appear.
   *
   * Omitted on almost everything, and that is the point: an area's navigation
   * is the area's navigation, and hiding screens per role would turn one
   * product into several that look alike. The exception is a screen an entire
   * role would get a `403` from -- offering a vyttech analyst the certificate
   * screen is offering them a refusal.
   *
   * This is presentation and nothing more. The backend refuses the request
   * whatever the browser believes, which is where the actual protection is;
   * see `filterNavigation` below.
   */
  readonly requires?: Permission;
}

const STAFF_NAV: readonly NavItem[] = [
  { to: PATHS.staff, label: 'Visão geral', end: true },
  { to: PATHS.staffReview, label: 'Fila de análise' },
  { to: PATHS.staffSubmissions, label: 'Envios do mês' },
  { to: PATHS.staffMailbox, label: 'Caixa de entrada' },
  { to: PATHS.staffCompanies, label: 'Empresas clientes' },
  // Right beside the companies, because a group is companies: it is where
  // somebody goes to see which customers belong together, and to put one in.
  { to: PATHS.staffGroups, label: 'Grupos econômicos' },
  // Next to the companies rather than a panel inside them. An operadora
  // belongs to no customer, and a contract has to point at one -- so the
  // moment somebody needs to register one is the moment the contract form has
  // nothing to choose, and "go to the screen next door" is an instruction that
  // can be given. "Scroll to the bottom of the companies screen" was not.
  { to: PATHS.staffInsurers, label: 'Operadoras' },
  { to: PATHS.staffCredentials, label: 'Credenciais de integração' },
  {
    to: PATHS.staffCertificates,
    label: 'Certificado digital',
    // The administrator's alone. An analyst administers no credentials, and
    // this is the credential the platform itself signs with.
    requires: Permission.CERTIFICATES_READ,
  },
  // No permission on this one, unlike the certificate screen above it. Both
  // vyttech roles hold `esocial:read`: watching this bridge is the analyst's
  // daily job, and testing a connection is not administering the credential it
  // uses.
  { to: PATHS.staffEsocial, label: 'Conexão eSocial' },
  // Next to the connection, and unpermissioned for the same reason: both
  // vyttech roles hold `esocial:read`, and watching what the government has
  // filed for a customer is an analyst's work rather than an administrator's.
  { to: PATHS.staffEsocialEvents, label: 'Eventos eSocial' },
  { to: PATHS.staffUsers, label: 'Usuários' },
  { to: PATHS.staffAudit, label: 'Auditoria' },
];

const CUSTOMER_NAV: readonly NavItem[] = [
  { to: PATHS.customer, label: 'Visão geral', end: true },
  { to: PATHS.customerBeneficiaries, label: 'Beneficiários' },
  { to: PATHS.customerMovements, label: 'Movimentações' },
  { to: PATHS.customerStatus, label: 'Acompanhamento' },
  { to: PATHS.customerSpreadsheetUpload, label: 'Envio de planilha' },
];

export const NAVIGATION: Record<Area, readonly NavItem[]> = {
  staff: STAFF_NAV,
  customer: CUSTOMER_NAV,
};

/**
 * The items one principal may actually reach.
 *
 * Applied in the layout rather than baked into the lists above, so the lists
 * stay readable as "what the back-office is" and the filtering is one
 * expression somebody can check.
 */
export function filterNavigation(
  items: readonly NavItem[],
  permissions: readonly Permission[],
): readonly NavItem[] {
  return items.filter((item) => item.requires === undefined || permissions.includes(item.requires));
}
